import {
    CONTACT_DISPLAY_FIELD_ORDER,
    CONTACT_FIELD_DISPLAY_LABELS,
    CONTACT_ID_UPLOAD_FIELDS,
} from '../constants/contactFields';
import type {
    ContactFormValues,
    IntakeSummaryPdfData,
    IntakeSummaryPdfFormSubmissionField,
} from '../types/formSubmissions';
import { getIntakeFormTitle } from '../util';
import { getContactBlockQuestions } from './contactBlock';
import { formatContactBlockDisplayValue } from './contactBlockDisplay';
import { getContactName } from './contactNames';
import {
    formatContactFormFieldDisplayValue,
    formatIntakeFieldDisplayValue,
    formatIntakeTableDisplayValue,
    getQuestionSchemaType,
    parseQuestionSchema,
} from './intakeFieldFormatting';
import { getDelimitedFileIds } from './intakeUploads';

const MAX_CONTACT_COLUMNS_PER_TABLE = 4;

export interface IntakeSummaryOptions {
    showEmptyAnswersInIntakeSummary: boolean;
    showContactsAsColumnsInIntakeSummary: boolean;
    /** The screen links uploaded files; the PDF leaves uploads out */
    includeUploads?: boolean;
}

export interface IntakeSummaryRow {
    label: string;
    value: string;
    /** Set on upload answers, one per uploaded file */
    fileIds?: string[];
    isCriticalQuestion?: boolean;
}

export interface IntakeSummaryTable {
    title: string;
    columns: string[];
    rows: string[][];
    isCriticalQuestion?: boolean;
}

export interface IntakeSummaryContactEntry {
    title?: string;
    items: IntakeSummaryItem[];
}

/** The clients, or a contact block's entries: one entry per contact */
export interface IntakeSummaryContacts {
    title?: string;
    isCriticalQuestion?: boolean;
    entries: IntakeSummaryContactEntry[];
    /** Known row order for contact columns, for labels no single contact has all of */
    rowLabelOrder?: string[];
}

export interface IntakeSummaryContactGridRow {
    label: string;
    /** One value per contact column; null when that contact has no row with this label */
    values: (string | null)[];
}

/** Contacts side by side: one column per contact, one row per label */
export interface IntakeSummaryContactGrid {
    contactTitles: string[];
    rows: IntakeSummaryContactGridRow[];
}

/** Consecutive answers form one rows item; tables and contacts sit between them where the form shows them */
export type IntakeSummaryItem =
    | { kind: 'rows'; rows: IntakeSummaryRow[] }
    | { kind: 'table'; table: IntakeSummaryTable }
    | { kind: 'contacts'; contacts: IntakeSummaryContacts }
    | { kind: 'contactGrid'; grid: IntakeSummaryContactGrid };

export interface IntakeSummarySection {
    title: string;
    items: IntakeSummaryItem[];
}

export interface IntakeSummaryData {
    heading: string;
    sections: IntakeSummarySection[];
}

export function isEmptyIntakeSummaryValue(value: string | null | undefined): boolean {
    return value === null || value === undefined || String(value).trim() === '';
}

export function toRowsItems(rows: IntakeSummaryRow[]): IntakeSummaryItem[] {
    return rows.length > 0 ? [{ kind: 'rows', rows }] : [];
}

/** Adds the row to the rows item it follows, or starts a new one after a table or contacts */
export function appendSummaryRow(items: IntakeSummaryItem[], row: IntakeSummaryRow): void {
    const lastItem = items[items.length - 1];
    if (lastItem?.kind === 'rows') {
        lastItem.rows.push(row);
    } else {
        items.push({ kind: 'rows', rows: [row] });
    }
}

export function getItemRows(items: IntakeSummaryItem[]): IntakeSummaryRow[] {
    return items.flatMap((item) => (item.kind === 'rows' ? item.rows : []));
}

/** Keys rows by label, numbering repeats of a label so they stay separate rows */
function getContactRowKeys(labels: string[]): string[] {
    const occurrencesByLabel = new Map<string, number>();
    return labels.map((label) => {
        const occurrence = occurrencesByLabel.get(label) ?? 0;
        occurrencesByLabel.set(label, occurrence + 1);
        return `${label}\u0000${occurrence}`;
    });
}

/**
 * Merges each contact's rows into one row per label. Labels in `labelOrder` keep that order.
 * Any other label only some contacts have is placed before the next label it precedes for
 * that contact, so the fields keep the order the contacts list them in.
 */
function mergeContactRows(
    contactRows: IntakeSummaryRow[][],
    labelOrder: string[] = [],
): IntakeSummaryContactGridRow[] {
    const labelOrderKeys = getContactRowKeys(labelOrder);
    const merged: (IntakeSummaryContactGridRow & { key: string })[] = labelOrder.map(
        (label, index) => ({
            key: labelOrderKeys[index],
            label,
            values: contactRows.map(() => null),
        }),
    );

    contactRows.forEach((rows, contactIndex) => {
        const keys = getContactRowKeys(rows.map(({ label }) => label));
        rows.forEach(({ label, value }, rowIndex) => {
            let mergedIndex = merged.findIndex((row) => row.key === keys[rowIndex]);
            if (mergedIndex === -1) {
                const nextPlacedIndex = keys
                    .slice(rowIndex + 1)
                    .map((key) => merged.findIndex((row) => row.key === key))
                    .find((index) => index !== -1);
                mergedIndex = nextPlacedIndex ?? merged.length;
                merged.splice(mergedIndex, 0, {
                    key: keys[rowIndex],
                    label,
                    values: contactRows.map(() => null),
                });
            }
            merged[mergedIndex].values[contactIndex] = value;
        });
    });

    return merged
        .filter(({ values }) => values.some((value) => value !== null))
        .map(({ label, values }) => ({ label, values }));
}

/**
 * Row order for contact columns, taken from each contact's rows before empty answers are
 * dropped, so a question one contact answered and another didn't still has its place
 */
export function getUnfilteredRowLabelOrder(unfilteredContactRows: IntakeSummaryRow[][]): string[] {
    return mergeContactRows(unfilteredContactRows).map(({ label }) => label);
}

function toContactColumnItems(contacts: IntakeSummaryContacts): IntakeSummaryItem[] {
    const { entries } = contacts;
    if (entries.length < 2) {
        return [{ kind: 'contacts', contacts }];
    }

    const items: IntakeSummaryItem[] = [];
    for (let start = 0; start < entries.length; start += MAX_CONTACT_COLUMNS_PER_TABLE) {
        const chunk = entries.slice(start, start + MAX_CONTACT_COLUMNS_PER_TABLE);
        items.push({
            kind: 'contactGrid',
            grid: {
                contactTitles: chunk.map(
                    (entry, index) => entry.title ?? `Client ${start + index + 1}`,
                ),
                rows: mergeContactRows(
                    chunk.map((entry) => getItemRows(entry.items)),
                    contacts.rowLabelOrder,
                ),
            },
        });
    }

    // Tables can't be split into contact columns, so they follow the grid with the contact named
    for (const entry of entries) {
        for (const item of entry.items) {
            if (item.kind !== 'table') continue;
            items.push({
                kind: 'table',
                table: {
                    ...item.table,
                    title: entry.title ? `${entry.title} - ${item.table.title}` : item.table.title,
                },
            });
        }
    }

    return items;
}

/** Lays out contacts with two or more entries as grids, one column per contact */
export function applyContactColumns(
    sections: IntakeSummarySection[],
    options: IntakeSummaryOptions,
): IntakeSummarySection[] {
    if (!options.showContactsAsColumnsInIntakeSummary) {
        return sections;
    }

    return sections.map((section) => ({
        ...section,
        items: section.items.flatMap((item) =>
            item.kind === 'contacts' ? toContactColumnItems(item.contacts) : [item],
        ),
    }));
}

function getClientTitle(formValues: ContactFormValues, index: number): string {
    const firstName = formValues.firstName.trim();
    const middleName = formValues.middleName?.trim() ?? '';
    const lastName = formValues.lastName.trim();

    if (firstName && lastName) {
        return getContactName({ firstName, middleName, lastName });
    }

    const partialName = [firstName, middleName, lastName].filter(Boolean).join(' ');
    return partialName || `Client #${index + 1}`;
}

function getUploadRow(
    label: string,
    fileIds: string[],
    isCriticalQuestion?: boolean,
): IntakeSummaryRow {
    return {
        label,
        value: fileIds.length > 0 ? fileIds.join(', ') : '',
        fileIds,
        isCriticalQuestion,
    };
}

function getContactFormSummaryRows(
    formValues: ContactFormValues,
    options: IntakeSummaryOptions,
): IntakeSummaryRow[] {
    const rows = CONTACT_DISPLAY_FIELD_ORDER.flatMap<IntakeSummaryRow>((key) => {
        const value = formatContactFormFieldDisplayValue(key, formValues[key]);
        return value ? [{ label: CONTACT_FIELD_DISPLAY_LABELS[key], value }] : [];
    });

    if (options.includeUploads) {
        for (const { field, title } of CONTACT_ID_UPLOAD_FIELDS) {
            const fileIds = getDelimitedFileIds(formValues[field]);
            if (fileIds.length > 0) rows.push(getUploadRow(title, fileIds));
        }
    }

    return rows;
}

function getClientsSection(
    contactFormValues: ContactFormValues[],
    options: IntakeSummaryOptions,
): IntakeSummarySection | null {
    const hasMultipleClients = contactFormValues.length > 1;
    const entries = contactFormValues.flatMap<IntakeSummaryContactEntry>((formValues, index) => {
        const rows = getContactFormSummaryRows(formValues, options);
        if (rows.length === 0) return [];
        return [
            {
                title: hasMultipleClients ? getClientTitle(formValues, index) : undefined,
                items: toRowsItems(rows),
            },
        ];
    });

    if (entries.length === 0) return null;

    return {
        title: 'Client Information',
        items: [
            {
                kind: 'contacts',
                contacts: { entries, rowLabelOrder: Object.values(CONTACT_FIELD_DISPLAY_LABELS) },
            },
        ],
    };
}

function getFieldTitle(field: IntakeSummaryPdfFormSubmissionField, fallback = ''): string {
    return field.title ?? field.name ?? fallback;
}

function parseTableShapedRows(value?: string | null): Record<string, unknown>[] | null {
    try {
        const parsed: unknown = value ? JSON.parse(value) : null;
        if (!Array.isArray(parsed)) return null;
        const rows = parsed.filter(
            (row): row is Record<string, unknown> =>
                !!row && typeof row === 'object' && !Array.isArray(row),
        );
        return rows.length > 0 ? rows : null;
    } catch {
        return null;
    }
}

/** Table answers whose question has no table schema; the columns come from the answer's keys */
function getSchemalessTable(
    field: IntakeSummaryPdfFormSubmissionField,
    rows: Record<string, unknown>[],
): IntakeSummaryTable {
    const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
    return {
        title: getFieldTitle(field, 'Table'),
        columns,
        rows: rows.map((row) => columns.map((key) => formatIntakeFieldDisplayValue(row[key]))),
        isCriticalQuestion: field.isCriticalQuestion ?? undefined,
    };
}

function getTableItems(
    table: IntakeSummaryTable,
    options: IntakeSummaryOptions,
): IntakeSummaryItem[] {
    if (table.columns.length === 0) return [];
    if (!options.showEmptyAnswersInIntakeSummary && table.rows.length === 0) return [];
    return [{ kind: 'table', table }];
}

/** A table question's answer as a summary table, or null when the question has no columns */
export function getTableFieldSummary(
    field: IntakeSummaryPdfFormSubmissionField,
): IntakeSummaryTable | null {
    const { columns, rows } = formatIntakeTableDisplayValue(
        field.submissionResponse,
        field.questionSchema,
    );
    if (columns.length === 0) {
        return null;
    }

    return {
        title: getFieldTitle(field, 'Table'),
        columns: columns.map((column) => column.label ?? ''),
        rows,
        isCriticalQuestion: field.isCriticalQuestion ?? undefined,
    };
}

function getContactBlockItems(
    field: IntakeSummaryPdfFormSubmissionField,
    options: IntakeSummaryOptions,
): IntakeSummaryItem[] {
    const { showEmptyAnswersInIntakeSummary, includeUploads } = options;
    const entries = formatContactBlockDisplayValue(
        field.submissionResponse,
        field.questionSchema,
    ).flatMap<IntakeSummaryContactEntry>((entry) => {
        const items: IntakeSummaryItem[] = [];
        for (const item of entry.items) {
            if (item.kind === 'text') {
                if (showEmptyAnswersInIntakeSummary || !isEmptyIntakeSummaryValue(item.value)) {
                    appendSummaryRow(items, { label: item.label, value: item.value });
                }
            } else if (item.kind === 'files') {
                if (
                    includeUploads &&
                    (showEmptyAnswersInIntakeSummary || item.fileIds.length > 0)
                ) {
                    appendSummaryRow(items, getUploadRow(item.label, item.fileIds));
                }
            } else {
                items.push(
                    ...getTableItems(
                        {
                            title: item.label,
                            columns: item.table.columns.map((column) => column.label ?? ''),
                            rows: item.table.rows,
                        },
                        options,
                    ),
                );
            }
        }
        return items.length > 0 ? [{ title: entry.title, items }] : [];
    });

    if (entries.length === 0) return [];

    const schema = parseQuestionSchema(field.questionSchema);
    return [
        {
            kind: 'contacts',
            contacts: {
                title: getFieldTitle(field),
                isCriticalQuestion: field.isCriticalQuestion ?? undefined,
                entries,
                // Entries only list answered contact fields and their visible questions
                rowLabelOrder: [
                    ...Object.values(CONTACT_FIELD_DISPLAY_LABELS),
                    ...(schema
                        ? getContactBlockQuestions(schema).map((question) => question.title)
                        : []),
                ],
            },
        },
    ];
}

function getFieldItems(
    field: IntakeSummaryPdfFormSubmissionField,
    options: IntakeSummaryOptions,
): IntakeSummaryItem[] {
    const type = getQuestionSchemaType(field.questionSchema);
    const isCriticalQuestion = field.isCriticalQuestion ?? undefined;

    if (type === 'contactInfoSection') {
        return getContactBlockItems(field, options);
    }

    if (type === 'table') {
        const table = getTableFieldSummary(field);
        return table ? getTableItems(table, options) : [];
    }

    const tableShapedRows = parseTableShapedRows(field.submissionResponse);
    if (tableShapedRows) {
        return getTableItems(getSchemalessTable(field, tableShapedRows), options);
    }

    if (type === 'upload') {
        const fileIds = getDelimitedFileIds(field.submissionResponse);
        if (!options.includeUploads) return [];
        if (!options.showEmptyAnswersInIntakeSummary && fileIds.length === 0) return [];
        return toRowsItems([getUploadRow(getFieldTitle(field), fileIds, isCriticalQuestion)]);
    }

    const value = formatIntakeFieldDisplayValue(field.submissionResponse, field.questionSchema);
    if (!options.showEmptyAnswersInIntakeSummary && isEmptyIntakeSummaryValue(value)) {
        return [];
    }
    return toRowsItems([{ label: getFieldTitle(field), value, isCriticalQuestion }]);
}

/** Adds items to a section, joining rows that follow rows into one rows item */
function appendItems(items: IntakeSummaryItem[], newItems: IntakeSummaryItem[]): void {
    for (const item of newItems) {
        if (item.kind === 'rows') {
            item.rows.forEach((row) => appendSummaryRow(items, row));
        } else {
            items.push(item);
        }
    }
}

/**
 * What the intake summary shows, in order: the clients, then each form group with its
 * questions, tables and contact blocks in question order. The prospect's intake screen and
 * the summary PDF both render this, so they show the same answers in the same places.
 */
export function buildIntakeSummaryData(
    data: IntakeSummaryPdfData,
    options: IntakeSummaryOptions,
): IntakeSummaryData {
    const groupedFields = new Map<string, IntakeSummaryPdfFormSubmissionField[]>();
    for (const field of data.formSubmissionFields) {
        const groupTitle = field.formGroupTitle ?? 'Other Information';
        groupedFields.set(groupTitle, [...(groupedFields.get(groupTitle) ?? []), field]);
    }

    const sections: IntakeSummarySection[] = [];

    const clientsSection = getClientsSection(data.contactFormValues, options);
    if (clientsSection) sections.push(clientsSection);

    for (const [groupTitle, fields] of groupedFields) {
        const items: IntakeSummaryItem[] = [];
        const sortedFields = [...fields].sort(
            (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        );
        for (const field of sortedFields) {
            appendItems(items, getFieldItems(field, options));
        }

        if (items.length > 0) {
            sections.push({ title: groupTitle, items });
        }
    }

    const primaryContact = data.contactFormValues[0];
    const formTitle = getIntakeFormTitle(data.matterType);
    return {
        heading: primaryContact
            ? `${primaryContact.lastName}, ${primaryContact.firstName}: ${formTitle}`
            : formTitle,
        sections: applyContactColumns(sections, options),
    };
}
